import React, { useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { PartyPopper, CalendarClock, Award } from "lucide-react";
import { Section, Card, Kpi, GenderLegend, Empty, PersonLine, C, axis, tip, fmtDate } from "./parts";

const yrs = (n) => `${n} yr${n > 1 ? "s" : ""}`;

const MilestoneList = ({ byMilestone, onOpen, today }) => {
    const [sel, setSel] = useState(byMilestone.find((m) => m.people.length)?.years ?? 1);
    const cur = byMilestone.find((m) => m.years === sel);
    return (
        <>
            <div className="flex flex-wrap gap-1.5 mb-3">
                {byMilestone.map((m) => (
                    <button key={m.years} onClick={() => setSel(m.years)} data-testid={`milestone-tab-${m.years}`}
                        className={`h-7 px-2.5 rounded-full text-xs font-semibold border transition-colors ${sel === m.years ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                        {yrs(m.years)} <span className="opacity-60">{m.people.length}</span>
                    </button>
                ))}
            </div>
            {cur.people.length ? (
                <div className="max-h-60 overflow-y-auto pr-1">
                    {cur.people.map((r) => <PersonLine key={r.p.user_id} r={r} onOpen={onOpen} right={<span className={r.thisYear < today ? "text-slate-400" : "text-[#25908A] font-medium"}>{fmtDate(r.thisYear)}{r.thisYear < today ? " · done" : ""}</span>} />)}
                </div>
            ) : <Empty>No one completes {yrs(sel)} this year.</Empty>}
        </>
    );
};

const AnniversaryCalendar = ({ calendar, today, onOpen }) => {
    const [m, setM] = useState(today.getMonth());
    const items = calendar[m].items;
    return (
        <>
            <div className="grid grid-cols-6 sm:grid-cols-12 gap-1 mb-3">
                {calendar.map((c, i) => (
                    <button key={c.month} onClick={() => setM(i)} data-testid={`anniv-month-${c.month}`}
                        className={`rounded-md py-1.5 text-center border transition-colors ${m === i ? "border-[#2EB4AC] bg-teal-50" : "border-slate-100 hover:border-slate-300"}`}>
                        <div className={`text-[10px] font-semibold ${i === today.getMonth() ? "text-[#25908A]" : "text-slate-500"}`}>{c.month}</div>
                        <div className="text-sm font-bold text-slate-900 tabular-nums">{c.items.length}</div>
                    </button>
                ))}
            </div>
            {items.length ? (
                <div className="grid sm:grid-cols-2 gap-x-6 max-h-60 overflow-y-auto pr-1" data-testid="anniv-month-list">
                    {items.map((r) => <PersonLine key={r.p.user_id} r={r} onOpen={onOpen} right={<><b>{fmtDate(r.thisYear)}</b> · {yrs(r.yearsThisYear)}</>} />)}
                </div>
            ) : <Empty>No work anniversaries in {calendar[m].month}.</Empty>}
        </>
    );
};

export const MilestonesSection = ({ m, today, onOpen }) => (
    <Section no="04" title="Employee Milestones" testid="wf-milestones">
        <div className="grid lg:grid-cols-3 gap-3">
            <Kpi label="Today's work anniversaries" value={m.todays.length} tone="text-[#25908A]" icon={<PartyPopper className="h-3 w-3" />} sub={m.todays.length ? m.todays.map((r) => r.p.name).slice(0, 3).join(", ") : "None today"} testid="ms-today" />
            <Kpi label="Upcoming (next 7 days)" value={m.upcoming.length} icon={<CalendarClock className="h-3 w-3" />} sub={m.upcoming[0] ? `Next: ${m.upcoming[0].p.name} · ${fmtDate(m.upcoming[0].next)}` : "None this week"} testid="ms-upcoming-count" />
            <Kpi label="Key milestones this year" value={m.byMilestone.reduce((a, x) => a + x.people.length, 0)} icon={<Award className="h-3 w-3" />} sub="Completing 1, 2, 3, 5 or 10 years" testid="ms-key-count" />
        </div>
        <div className="grid lg:grid-cols-3 gap-3">
            <Card title="Today & next 7 days" testid="ms-upcoming">
                {m.todays.length + m.upcoming.length ? (
                    <div className="max-h-72 overflow-y-auto pr-1">
                        {m.todays.map((r) => <PersonLine key={r.p.user_id} r={r} onOpen={onOpen} right={<span className="px-2 py-0.5 rounded-full bg-teal-50 text-[#25908A] font-semibold">Today · {yrs(r.yearsAtNext)}</span>} />)}
                        {m.upcoming.map((r) => <PersonLine key={r.p.user_id} r={r} onOpen={onOpen} right={<>{fmtDate(r.next)} · {yrs(r.yearsAtNext)} <span className="text-slate-400">in {r.daysUntil}d</span></>} />)}
                    </div>
                ) : <Empty>No work anniversaries in the next 7 days.</Empty>}
            </Card>
            <Card title="Completing 1 · 2 · 3 · 5 · 10 years" hint={`in ${today.getFullYear()}`} testid="ms-key">
                <MilestoneList byMilestone={m.byMilestone} onOpen={onOpen} today={today} />
            </Card>
            <Card title="Gender-wise milestone distribution" hint={`${today.getFullYear()}`} testid="ms-gender">
                {m.genderDist.some((x) => x.total) ? (
                    <>
                        <div className="h-52">
                            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 180 }}>
                                <BarChart maxBarSize={48} data={m.genderDist} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                                    <XAxis dataKey="label" {...axis} />
                                    <YAxis allowDecimals={false} {...axis} />
                                    <Tooltip {...tip} />
                                    <Bar dataKey="male" name="Male" stackId="g" fill={C.male} />
                                    <Bar dataKey="female" name="Female" stackId="g" fill={C.female} />
                                    <Bar dataKey="unset" name="Not set" stackId="g" fill={C.unset} radius={[3, 3, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <GenderLegend />
                    </>
                ) : <Empty>No key milestones this year.</Empty>}
            </Card>
        </div>
        <Card title="Monthly anniversary calendar" hint={`${today.getFullYear()} · click a month`} testid="ms-calendar">
            <AnniversaryCalendar calendar={m.calendar} today={today} onOpen={onOpen} />
        </Card>
    </Section>
);
